import { useNavigate } from 'react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { Lock, Plus, X } from 'lucide-react';
import { cn } from 'helpers/cn';
import {
  isOriginalVariantName,
  ORIGINAL_NAME_TAKEN,
  ORIGINAL_VARIANT_NAME,
  redirectUrlProblem,
} from 'helpers/variant-fields';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { useForm } from 'hooks/useForm';
import { useExperiment } from '@/state/experiments/useExperiment';
import { apiErrorMessage } from '@/state/api-error';
import { EXPERIMENT_TYPE_LABELS, TEST_EXPERIMENT_TYPES } from 'definitions';

type CreateExperimentModalProps = {
  isCreating: boolean;
  setIsCreating: (v: boolean) => void;
};

/**
 * What each type is, in one line.
 *
 * The type was a plain select, which made the most consequential choice in the dialog
 * look like the least. It decides what an experiment *is* and it rewrites the form
 * underneath it - URL Redirect needs a destination per variant - and none of that was
 * explained anywhere. Picking wrong meant creating the experiment and finding out.
 *
 * The Server-side line is one short sentence like its neighbours; which side assigns, and the
 * setup steps and code, are in the experiment's Settings (ServerSideConfig).
 */
export const TYPE_BLURB: Record<(typeof TEST_EXPERIMENT_TYPES)[number], string> = {
  'Multi Variant': 'Edit a page visually and split traffic between the versions.',
  'Server Side': 'Your code shows each variant; Pertento picks who sees it.',
  'URL Redirect': 'Send each variant to a different address.',
};

const FIELD_CLASS = 'border-input bg-field text-foreground h-9 rounded-lg text-body';

export const CreateExperimentModal = (props: CreateExperimentModalProps) => {
  const navigate = useNavigate();
  const { isCreating, setIsCreating } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { formRef, state: newExperiment, update } = useForm<NewExperimentType>(emptyExperiment);
  const { variants, type } = newExperiment;
  const { createExperiment } = useExperiment(0);

  const addVariant = () => {
    update((state: any) => ({
      ...state,
      variants: [...state.variants, { name: '', redirectUrl: '' }],
    }));
  };

  const removeVariant = (variant: any) => () => {
    update((state: any) => {
      return { ...state, variants: state.variants.filter((v: any) => v !== variant) };
    });
  };

  const setType = (next: (typeof TEST_EXPERIMENT_TYPES)[number]) => update((state: any) => ({ ...state, type: next }));

  /**
   * What is still missing, as a sentence.
   *
   * The submit button was disabled by a boolean and said nothing about why, which on a
   * form this size is a guessing game. Same rule as the rest of the app: a dead
   * control explains itself.
   */
  const blockingReason = (): string | undefined => {
    if (!newExperiment?.type) return 'Choose an experiment type';
    if (!newExperiment.name) return 'Give the experiment a name';
    if (!variants?.length) return 'Add at least one variant';
    if (variants.some((v) => !v.name?.trim())) return 'Every variant needs a name';
    if (variants.some((v) => isOriginalVariantName(v.name))) return ORIGINAL_NAME_TAKEN;
    if (type === 'URL Redirect') {
      if (variants.some((v) => !v.redirectUrl?.trim())) return 'Every variant needs a redirect destination';
      const urlProblem = variants.map((v) => redirectUrlProblem(v.redirectUrl?.trim())).find(Boolean);
      if (urlProblem) return urlProblem;
    }
    return undefined;
  };

  const reason = blockingReason();

  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      // A destination typed before switching away from URL Redirect is still in the
      // form state; it is hidden now, so it must not be sent (or refused) either.
      const submitted =
        type === 'URL Redirect'
          ? newExperiment
          : { ...newExperiment, variants: variants!.map(({ name }) => ({ name })) };
      const dbExperiment = await createExperiment(submitted);
      setIsCreating(false);
      navigate(`/experiments/${dbExperiment.id}`);
    } catch (error) {
      // The dialog stays open: everything typed is still here to correct.
      toast.error(apiErrorMessage(error, 'Could not create the experiment.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateVariant = (idx: number) => (ev: React.ChangeEvent<HTMLInputElement>) => {
    const { value, dataset } = ev.target;
    const { field } = dataset;
    update((state: any) => {
      const newVariants = [...variants!];
      newVariants[idx] = { ...newVariants[idx]!, [field as string]: value };
      return { ...state, variants: newVariants };
    });
  };

  return (
    <ConfirmDialog
      open={isCreating}
      title="Create experiment"
      description="Name it, choose how it runs, and list the variants. You can change all of this afterwards."
      confirmLabel="Create experiment"
      disabled={isSubmitting || !!reason}
      disabledReason={reason}
      onConfirm={onSubmit}
      onCancel={() => setIsCreating(false)}
    >
      <form ref={formRef} className="grid gap-4">
        {/* Type first, because it decides what the rest of the form asks for. */}
        <fieldset className="grid gap-2">
          <legend className="micro-label mb-2">How it runs</legend>
          <div className="grid gap-2 sm:grid-cols-3">
            {TEST_EXPERIMENT_TYPES.map((option) => {
              const isSelected = type === option;
              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => setType(option)}
                  className={cn(
                    'focus-visible:ring-ring rounded-card border p-3 text-left transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none',
                    isSelected
                      ? 'border-sel-line bg-sel-bg'
                      : 'border-input hover:bg-hover text-dim hover:text-foreground',
                  )}
                >
                  <span className={cn('block text-body font-medium', isSelected && 'text-foreground')}>
                    {EXPERIMENT_TYPE_LABELS[option]}
                  </span>
                  <span className="supporting mt-1 block">{TYPE_BLURB[option]}</span>
                </button>
              );
            })}
          </div>
          {/* `useForm` reads the value off a named input, so the choice is mirrored
              into a hidden one rather than duplicating the form's state. */}
          <input type="hidden" name="type" value={type} />
        </fieldset>

        <div className="grid gap-1.5">
          <label htmlFor="exp-name" className="micro-label">
            Name
          </label>
          <Input id="exp-name" name="name" placeholder="e.g. Homepage hero button" className={FIELD_CLASS} />
        </div>

        <div className="grid gap-1.5">
          <label htmlFor="exp-desc" className="micro-label">
            Hypothesis
          </label>
          <Textarea
            id="exp-desc"
            name="description"
            placeholder="What do you expect to change, and why?"
            className="border-input bg-field text-foreground min-h-[76px] resize-y rounded-lg text-body"
          />
        </div>

        {/* Variants sit in their own block: it is a list that grows, not a field. */}
        <fieldset className="border-line rounded-card grid gap-2 border p-3">
          <div className="flex items-center justify-between gap-2">
            <legend className="micro-label">Variants</legend>
            <button
              type="button"
              onClick={addVariant}
              className="border-input text-dim hover:bg-hover hover:text-foreground focus-visible:ring-ring flex min-h-7 items-center gap-1 rounded-lg border px-2 font-mono text-nano tracking-[0.1em] uppercase transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none"
            >
              <Plus className="size-3" strokeWidth={2} />
              Add variant
            </button>
          </div>

          {/* Original is inserted by the API, never typed here. The first row used to be
              labelled "Original", so people named it that and got two of them - and the
              dashboard recognises the control by that name. */}
          <div className="bg-field border-line flex min-h-11 items-center gap-2 rounded-lg border border-dashed px-2.5 py-1.5">
            <Lock className="text-dim size-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
            <div className="grid min-w-0 gap-0.5">
              <div className="flex items-center gap-2">
                <span className="text-foreground text-body font-medium">{ORIGINAL_VARIANT_NAME}</span>
                <span className="border-line bg-panel text-dim rounded-pill border px-2 py-0.5 font-mono text-nano tracking-[0.1em] uppercase">
                  control
                </span>
              </div>
              <span className="supporting">
                Added automatically: your page as it is today. Add the versions to test against it below.
              </span>
            </div>
          </div>

          <div className="grid gap-2">
            {variants!.map((variant, idx) => (
              <div key={idx} className="flex items-start gap-2">
                {/* The number is the variant's identity in every chart and table
                    afterwards, so it is shown rather than left to be counted. */}
                <span className="micro-label mt-2.5 w-4 shrink-0 text-right">{idx + 1}</span>

                <div className="grid min-w-0 flex-1 gap-2">
                  <Input
                    aria-label={`Variant ${idx + 1} name`}
                    placeholder={idx === 0 ? 'e.g. Bigger hero button' : `Variant ${idx + 1}`}
                    value={variant.name}
                    data-field="name"
                    onChange={updateVariant(idx)}
                    className={FIELD_CLASS}
                  />
                  {type === 'URL Redirect' && (
                    <Input
                      aria-label={`Variant ${idx + 1} redirect URL`}
                      inputMode="url"
                      placeholder="https://example.com/destination"
                      value={variant.redirectUrl}
                      data-field="redirectUrl"
                      onChange={updateVariant(idx)}
                      className={FIELD_CLASS}
                    />
                  )}
                </div>

                {variants!.length > 1 && (
                  <button
                    type="button"
                    aria-label={`Remove variant ${idx + 1}`}
                    onClick={removeVariant(variant)}
                    className="text-dim hover:text-danger hover:bg-hover focus-visible:ring-ring mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none"
                  >
                    <X className="size-4" strokeWidth={1.5} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </fieldset>
      </form>
    </ConfirmDialog>
  );
};

export type NewExperimentType = {
  type: (typeof TEST_EXPERIMENT_TYPES)[number];
  name: string;
  variants: { name: string; redirectUrl?: string }[];
};

export const emptyExperiment = { type: TEST_EXPERIMENT_TYPES[0], name: '', variants: [{ name: '', redirectUrl: '' }] };
